import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { companyAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

const PlanContext = createContext(null);

export const PlanProvider = ({ children }) => {
  const { user, billingEnabled } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    try {
      const res = await companyAPI.getSubscription();
      setSubscription(res.data);
    } catch {
      setSubscription(null);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // The email-confirmation banner must disappear by itself: the link is
  // usually opened in a second tab, so re-focusing the app re-reads the
  // subscription instead of leaving a stale "Staðfestu netfangið þitt".
  useEffect(() => {
    if (!user) return;
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, refresh]);

  const can = useCallback((feature) => {
    if (!subscription?.limits) return false;
    return !!subscription.limits[feature];
  }, [subscription]);

  const overLimit = subscription?.over_limit || false;
  const maxUsers = subscription?.limits?.max_users || 1;
  const currentUsers = subscription?.limits?.current_users || 0;
  const plan = subscription?.plan || 'solo';
  const planName = subscription?.plan_name || 'Einstaklingur';
  const subscriptionStatus = subscription?.subscription_status || 'none';
  // Beta flag override (2026-04-29): when billingEnabled=false at the auth
  // layer (i.e. user is not on the allowlist), forcibly hide every billing-
  // related affordance regardless of what the subscription endpoint says.
  // This also short-circuits trial/expired/past-due/pretrial banners and
  // the paywall trigger, since none of those should fire while billing is
  // gated. Reversible: when the env flag is dropped, billingEnabled returns
  // true and the original computed values surface again.
  const rawBillingVisible = subscription?.billing_visible ?? false;
  const billingVisible = billingEnabled && rawBillingVisible;
  const isPastDue = billingEnabled && (subscription?.is_past_due || false);
  const isRestricted = billingEnabled && (subscription?.is_restricted || false);
  const isTrialExpired = billingEnabled && (subscription?.is_trial_expired || false);
  const isPretrial = billingEnabled && (subscription?.is_pretrial ?? false);
  const needsCardSetup = billingEnabled && (subscription?.needs_card_setup ?? false);
  const trialEndsAt = subscription?.trial_ends_at || null;
  const trialDaysRemaining = subscription?.trial_days_remaining ?? null;
  const isGrandfatherTrial = subscription?.is_grandfather_trial ?? false;
  const gracePeriodDaysRemaining = subscription?.grace_period_days_remaining ?? null;
  const trialStarted = subscription?.trial_started ?? false;
  const pretrialLimits = subscription?.pretrial_limits ?? null;
  const emailVerified = subscription?.email_verified ?? true; // default true so banner doesn't flash before load
  // True once the user has ever received a Konto invoice. Used by the
  // restricted-banner to pick honest copy: a brand-new account that never
  // got a single invoice must NOT see "greiðsla hefur ekki borist" — that
  // implies an unpaid invoice exists. Show "klárðu uppsetningu" instead.
  const hasInvoiceHistory = Boolean(
    subscription?.last_invoice_number || subscription?.last_invoice_guid
  );
  // iter364: the pakki chosen at signup starts by itself at trial end.
  const autoActivate = subscription?.auto_activate ?? false;
  const autoActivatePlanName = subscription?.auto_activate_plan_name || null;
  const autoActivateMonthlyIsk = subscription?.auto_activate_monthly_isk ?? 0;
  const firstInvoiceDate = subscription?.first_invoice_date || null;

  return (
    <PlanContext.Provider value={{
      subscription, loading, can, refresh,
      overLimit, maxUsers, currentUsers, plan, planName,
      subscriptionStatus, billingVisible, isPastDue, isRestricted, gracePeriodDaysRemaining,
      isTrialExpired, trialEndsAt, trialDaysRemaining, isGrandfatherTrial, needsCardSetup,
      trialStarted, isPretrial, pretrialLimits, emailVerified, hasInvoiceHistory,
      autoActivate, autoActivatePlanName, autoActivateMonthlyIsk, firstInvoiceDate,
    }}>
      {children}
    </PlanContext.Provider>
  );
};

export const usePlan = () => useContext(PlanContext);
